/**
 * Lease state: the owner's licenses grouped by lease period, their active
 * leases, and the guarded lease-creation flow.
 *
 * Context rather than a hook because the lease list, the lease form and the
 * claims controller all act on the same two collections — the claims controller
 * calls `getLeasesByOwner` after a successful leasing claim, which is what
 * releases the licenses in the list the user is looking at.
 *
 * Reads go through `src/lib/api/license-leases.ts`. Both reads deliberately
 * SWALLOW their error into `undefined` state rather than toasting: they run on
 * mount for every visitor, and a transient upstream blip should render an empty
 * list, not a red banner.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { logger } from "@swarm/shared/logger";
import type {
  GetLeasesResponse,
  LicensesByPeriodResponse,
  RequestNewLeaseRequest,
} from "@swarm/shared/schemas";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { apiErrorMessage } from "@/lib/api/client";
import * as leasesApi from "@/lib/api/license-leases";
import { useToast } from "@/ui/shadcn/use-toast";

import { LicenseLeasesContext } from "./LicenseLeasesContext";
import {
  LicenseLeasesContextControllerActions,
  LicenseLeasesContextControllerProps,
  LicenseLeasesContextType,
  RequestNewLeaseFormSchema,
} from "./LicenseLeasesContext.types";

export const LicenseLeasesContextController = ({
  children,
}: LicenseLeasesContextControllerProps) => {
  const [actions, setActions] = useState<LicenseLeasesContextControllerActions>({
    requestNewLease: {
      isLoading: false,
      isError: false,
    },
  });

  const [licensesByOwner, setLicensesByOwner] = useState<LicensesByPeriodResponse | undefined>(
    undefined,
  );

  const [leasesByOwner, setLeasesByOwner] = useState<GetLeasesResponse | undefined>(undefined);

  const { toast } = useToast();

  const requestNewLeaseForm = useForm<z.infer<typeof RequestNewLeaseFormSchema>>({
    resolver: zodResolver(RequestNewLeaseFormSchema),
    defaultValues: {
      quantity: "1",
      duration: "18",
    },
    mode: "all",
  });

  async function requestNewLease(args: RequestNewLeaseRequest) {
    setActions((prev) => ({
      ...prev,
      requestNewLease: {
        isLoading: true,
        isError: false,
      },
    }));

    try {
      await leasesApi.requestNewLease(args);

      setActions((prev) => ({
        ...prev,
        requestNewLease: {
          isLoading: false,
          isError: false,
        },
      }));

      // Refresh both licenses and leases data after successful lease creation
      getLicensesByOwner(args.owner);
      getLeasesByOwner(args.owner);

      toast({
        title: "Success",
        variant: "accent",
        description: <p className="mb-0">Your license lease request was submitted successfully.</p>,
      });
    } catch (error) {
      logger.error(
        { err: error instanceof Error ? { message: error.message } : error },
        "lease creation request failed",
      );

      const message = apiErrorMessage(error, "Unknown error occurred");

      setActions((prev) => ({
        ...prev,
        requestNewLease: {
          isLoading: false,
          isError: true,
          error: message,
        },
      }));

      toast({
        title: "Error",
        variant: "destructive",
        description: <p className="mb-0">While submitting your lease request: {message}</p>,
      });
    }
  }

  async function getLicensesByOwner(owner: string) {
    try {
      const response = await leasesApi.getLicensesByOwner(owner);

      setLicensesByOwner(response);

      return response;
    } catch (error) {
      logger.error(
        { owner, err: error instanceof Error ? { message: error.message } : error },
        "failed to fetch licenses by owner",
      );

      setLicensesByOwner(undefined);

      return undefined;
    }
  }

  async function getLeasesByOwner(owner: string) {
    try {
      const response = await leasesApi.getLeasesByOwner(owner);

      setLeasesByOwner(response);

      return response;
    } catch (error) {
      logger.error(
        { owner, err: error instanceof Error ? { message: error.message } : error },
        "failed to fetch leases by owner",
      );

      setLeasesByOwner(undefined);

      return undefined;
    }
  }

  const props: LicenseLeasesContextType = {
    getLicensesByOwner,
    getLeasesByOwner,
    licensesByOwner,
    leasesByOwner,
    requestNewLease,
    requestNewLeaseForm,
    actions,
  };

  return <LicenseLeasesContext.Provider value={props}>{children}</LicenseLeasesContext.Provider>;
};
